import { useState } from 'react'
import Papa from 'papaparse'
import { useGetAuthenticatedUser } from '../../../Flux/Selector/index.ts'
import type { DraftState } from '../../../Model/OrderWizard/OrderWizardDraft.ts'
import { fileHandle } from '../../../Services/OrderWizard/fileHandles.ts'
import { SHEET_ROLES } from '../../../Services/OrderWizard/sheet/sheetMapping.ts'
import type { SupportRequest } from '../../../Services/OrderWizard/sheetSupport.ts'
import { fastqNamesOf, sendSheetToSupport, sheetSupportTrigger, supportAvailable } from '../../../Services/OrderWizard/sheetSupport.ts'
import { messageOf } from '../../../Flux/Epic/OrderWizard/messages.ts'
import { Callout } from './Primitives.tsx'

type SendState = { status: 'idle' | 'sending' } | { status: 'sent'; note: string } | { status: 'failed'; error: string }

const SendSheetBody = ({ request, orderId }: { request: SupportRequest; orderId?: string }) => {
  const user = useGetAuthenticatedUser()
  const [consent, setConsent] = useState(false)
  const [state, setState] = useState<SendState>({ status: 'idle' })
  const send = async () => {
    /* Sentry queues an event it cannot send and reports success; offline, nothing would reach support */
    if (typeof navigator !== 'undefined' && navigator.onLine === false) {
      setState({ status: 'failed', error: 'You are offline, so it was not sent. Send it again once you are back online.' })
      return
    }
    setState({ status: 'sending' })
    try {
      const payload = await sendSheetToSupport(request, {
        orderId,
        user: user ? { name: `${user.firstName} ${user.lastName}`.trim(), email: user.email } : undefined
      })
      setState({
        status: 'sent',
        note: payload.trimmed
          ? `The first ${payload.trimmed.sent} of ${payload.trimmed.total} rows were handed to our error reporting: the file is too large to send whole. Thank you.`
          : 'Handed to our error reporting. Thank you — we will look at the format.'
      })
    } catch (error) {
      setState({ status: 'failed', error: `It could not be sent: ${messageOf(error)}` })
    } finally {
      setConsent(false)
    }
  }
  return (
    <Callout className="space-y-2 rounded-lg bg-white p-3 text-sm" data-testid="wizard-send-sheet">
      <p className="font-medium text-slate-900">We couldn’t match this sheet to your files. Send it to us so we can support your format.</p>
      <label className="flex items-start gap-2 text-slate-700">
        <input
          type="checkbox"
          className="checkbox checkbox-sm mt-0.5"
          checked={consent}
          data-testid="wizard-send-sheet-consent"
          onChange={(event) => setConsent(event.target.checked)}
        />
        <span>I agree to send this file to spcfy support (it may contain sample names and coordinates)</span>
      </label>
      <p className="text-xs text-slate-500">
        It reaches spcfy support through our error reporting (Sentry), with your name and email, the columns read and your FASTQ file names.
      </p>
      <div className="flex flex-wrap items-center gap-2">
        <button
          type="button"
          className="btn btn-sm"
          disabled={!consent || state.status === 'sending'}
          data-testid="wizard-send-sheet-send"
          onClick={() => void send()}
        >
          {state.status === 'sending' ? 'Sending…' : 'Send'}
        </button>
        {state.status === 'sent' && (
          <span className="text-green-700" data-testid="wizard-send-sheet-result">
            {state.note}
          </span>
        )}
        {state.status === 'failed' && (
          <span className="text-red-700" data-testid="wizard-send-sheet-result">
            {state.error}
          </span>
        )}
      </div>
    </Callout>
  )
}

/** "Send us your file", where sending is possible at all: without a Sentry DSN (local development) it is not offered. */
export const SendSheetCard = ({ request, orderId }: { request: SupportRequest; orderId?: string }) =>
  supportAvailable() ? <SendSheetBody request={request} orderId={orderId} /> : null

const mappedRoles = (mapping: NonNullable<DraftState['sheet']>['mapping']): Record<string, string> =>
  Object.fromEntries(
    [...SHEET_ROLES.map((role) => [role, mapping[role]] as const), ...Object.entries(mapping.metadata)].filter(
      (entry): entry is [string, string] => !!entry[1]
    )
  )

/** The dropped sample sheet, when the wizard could not use it. */
export const DroppedSheetSupport = ({ draft }: { draft: DraftState }) => {
  const trigger = sheetSupportTrigger(draft)
  if (!trigger) {
    return null
  }
  const sheet = draft.sheet?.fileKey === trigger.fileKey ? draft.sheet : undefined
  const file = fileHandle(draft.id, trigger.fileKey)
  const source: SupportRequest['source'] | undefined = file
    ? { kind: 'file', file }
    : sheet
      ? { kind: 'text', text: Papa.unparse({ fields: sheet.headers, data: sheet.rows }) }
      : undefined
  if (!source) {
    return null
  }
  return (
    <SendSheetCard
      orderId={draft.orderId}
      request={{
        name: trigger.name,
        source,
        table: sheet ? { headers: sheet.headers, rows: sheet.rows } : undefined,
        columns: { headers: sheet?.headers ?? [], mapped: sheet ? mappedRoles(sheet.mapping) : undefined },
        stats: trigger.stats,
        fileNames: fastqNamesOf(draft)
      }}
    />
  )
}
